import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ModifierOption, ModifierGroup, Modifier } from '../../models/order';
import { Store } from 'src/app/models/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { log } from 'console';

@Component({
  selector: 'app-modifier-list',
  templateUrl: './modifier-list.component.html',
  styleUrls: ['./modifier-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModifierListComponent implements OnInit {

  @Input() modifierGroups: ModifierGroup[];

  _selectedModifiers: Modifier[];

  @Input() set selectedModifiers(m: Modifier[]) {
    if (m.length === 0) {
      this._selectedModifiers = []
    }

    if (!this._selectedModifiers) {
      this._selectedModifiers = m
    }

  }

  get selectedModifiers(): Modifier[] { return this._selectedModifiers }

  @Output() modifiersChange = new EventEmitter<Modifier[]>();

  @Output() isValid = new EventEmitter<boolean>();

  // @Input() isPizza:Boolean;

  @Input() store: Store;

  pizzaOptions = {
    "ADD": "WHOLE",
    "H1": "LEFT",
    "H2": "RIGHT"
  }

  modOptions = {
    "No": "NO",
    "Lite": "LITE",
    "Extra": "EXTRA",
    "On Side": "ON SIDE",
    "Sub": "SUB",
  }

  uiSetting: any;
  upcharge_half: boolean = false;
  modifieId: any;

  modGroupCount: Map<number, number> = new Map();

  constructor(private snackBar: MatSnackBar) { }

  ngOnInit() {
    this.uiSetting = this.store.ui_settings;
    this.checkAllModGroupValidity();
  }

  checkAllModGroupValidity() {
    const validIndependent = this.checkModGroupValidity(false);
    const validDependent = this.checkDependentModGroupValidity(false);
    const validSub = this.checkSubModifierValidity();
    const isValid = validIndependent && validDependent && validSub;
    this.isValid.emit(isValid);
  }

  /** True when the sub-modifier feature is enabled for this store. */
  get subModifiersEnabled(): boolean {
    return !!this.store?.isEnabled('sub_modifiers_enabled');
  }

  /** Every required nested group under a selected option must have its
   *  minimum picks. Returns true (no-op) when the flag is off so legacy
   *  stores are unaffected. */
  private checkSubModifierValidity(): boolean {
    if (!this.subModifiersEnabled) {
      return true;
    }
    for (const m of this._selectedModifiers) {
      const option = m.modifier as ModifierOption;
      const groups = option?.sub_modifier_groups;
      if (Array.isArray(groups) && groups.length) {
        if (!this.validateSubBranch(option, groups)) {
          return false;
        }
      }
    }
    return true;
  }

  private validateSubBranch(parentOption: ModifierOption, groups: ModifierGroup[]): boolean {
    for (const g of groups || []) {
      // Required-ness is governed solely by is_required; required quantity is
      // max_mods (or 1 when unlimited / -1) — mirrors the top-level gate.
      const requiredQty = g.is_required ? (g.max_mods === -1 ? 1 : g.max_mods) : 0;
      if (requiredQty > 0) {
        const count = this._selectedModifiers.filter(
          (m) => m.modifier?.modifier_group === g.id && m.parentModifierId === parentOption.id,
        ).length;
        if (count < requiredQty) {
          return false;
        }
      }
      for (const o of g.options || []) {
        const selected = this._selectedModifiers.some(
          (m) => m.modifier?.id === o.id && m.parentModifierId === parentOption.id,
        );
        if (selected && Array.isArray(o.sub_modifier_groups) && o.sub_modifier_groups.length) {
          if (!this.validateSubBranch(o, o.sub_modifier_groups)) {
            return false;
          }
        }
      }
    }
    return true;
  }

  /**
   * Toggle a nested sub-modifier option. Routes through the same flat
   * `_selectedModifiers` array as top-level picks, tagging each nested entry
   * with `parentModifierId` so the backend can record the hierarchy and the
   * UI can scope counts per branch. Max enforcement is scoped to
   * (parentOption, group). Deselecting a parent cascades to its whole subtree.
   */
  onSubModifierToggle(event: { option: ModifierOption; group: ModifierGroup; parentOption: ModifierOption }) {
    if (!this.subModifiersEnabled) {
      return;
    }
    const { option, group, parentOption } = event;
    const existing = this._selectedModifiers.find(
      (m) => m.modifier?.id === option.id && m.parentModifierId === parentOption.id,
    );

    if (existing) {
      this.removeSubModifierBranch(option.id, parentOption.id);
    } else {
      if (group.max_mods > 0) {
        const count = this._selectedModifiers.filter(
          (m) => m.modifier?.modifier_group === group.id && m.parentModifierId === parentOption.id,
        ).length;
        if (count >= group.max_mods) {
          this.snackBar.open(
            `${group.name}: you can choose at most ${group.max_mods}.`,
            'OK',
            { duration: 2000, verticalPosition: 'top' },
          );
          return;
        }
      }
      const mod = new Modifier();
      mod.modifier = Object.assign(new ModifierOption(), option);
      mod.quantity = 1;
      mod.mod_type = 'ADD';
      mod.parentModifierId = parentOption.id;
      this._selectedModifiers.push(mod);
    }

    this.modifiersChange.emit(this._selectedModifiers);
    this.checkAllModGroupValidity();
  }

  /** Remove a specific nested pick and its entire subtree of descendants. */
  private removeSubModifierBranch(optionId: number, parentOptionId: number) {
    this._selectedModifiers = this._selectedModifiers.filter(
      (m) => !(m.modifier?.id === optionId && m.parentModifierId === parentOptionId),
    );
    this.removeDescendantsOf(optionId);
  }

  /** Recursively remove every selection parented (directly or transitively)
   *  to `parentOptionId`. Used on nested toggle-off and top-level deselect. */
  private removeDescendantsOf(parentOptionId: number) {
    const childIds = this._selectedModifiers
      .filter((m) => m.parentModifierId === parentOptionId)
      .map((m) => m.modifier?.id)
      .filter((id) => id != null) as number[];
    if (!childIds.length) {
      return;
    }
    this._selectedModifiers = this._selectedModifiers.filter(
      (m) => m.parentModifierId !== parentOptionId,
    );
    childIds.forEach((id) => this.removeDescendantsOf(id));
  }

  checkModGroupValidity(emit = true): boolean {
    const requiredGroups = this.modifierGroups
      .filter(m =>
        m.is_required &&
        (!m.is_independent || !this.store?.feature_flag?.['mod_group_dependency_feature']) &&
        m.options?.length > 0 &&
        m.options.some(mod => !mod['is_pos_only'])
      )
      .map(m => ({ id: m.id, requiredQty: m.max_mods }));

    const selectedModifiersByGroup: { [key: number]: number } = {};

    this._selectedModifiers.forEach(m => {
      const groupId = m.modifier.modifier_group;
      const quantity = m.quantity || 1;
      selectedModifiersByGroup[groupId] = (selectedModifiersByGroup[groupId] || 0) + quantity;
    });

    const isValid = requiredGroups.every(group => {
      const selectedQty = selectedModifiersByGroup[group.id] || 0;
      const requiredQty = group.requiredQty;

      if (requiredQty === -1) {
        return selectedQty > 0;
      }

      return selectedQty >= requiredQty;
    });

    if (emit) this.isValid.emit(isValid);
    return isValid;
  }

  checkDependentModGroupValidity(emit = true): boolean {
    const requiredGroups = this.modifierGroups
      .filter(group =>
        group.is_required &&
        group.is_independent &&
        this.store?.feature_flag?.['mod_group_dependency_feature'] &&
        group.options?.length > 0 &&
        group.options.some(mod => !mod['is_pos_only'])
      )
      .map(group => ({
        id: group.id,
        requiredQty: group.max_mods
      }));

    const selectedModifiersByGroup: { [groupId: number]: Set<number> } = {};

    this._selectedModifiers.forEach(mod => {
      const groupId = mod.modifier.modifier_group;
      if (!selectedModifiersByGroup[groupId]) {
        selectedModifiersByGroup[groupId] = new Set();
      }
      selectedModifiersByGroup[groupId].add(mod.modifier.id);
    });

    const isValid = requiredGroups.every(group => {
      const selectedOptions = selectedModifiersByGroup[group.id]?.size || 0;
      const requiredQty = group.requiredQty;

      if (requiredQty === -1) {
        return selectedOptions > 0;
      }

      return selectedOptions >= requiredQty;
    });

    if (emit) this.isValid.emit(isValid);
    return isValid;
  }



  onModTypeChange(event, modifier) {
    this.modifieId = modifier.id;
    if (event.value == 'H1' || event.value == 'H2') {
      this.upcharge_half = true;
    } else {
      this.upcharge_half = false;
    }

    let selectedMod = this.getSelected(modifier);
    selectedMod.mod_type = event.value;
    this.modifierGroups.forEach(group => {
      group.options.forEach(mod => {
        if (mod.id === modifier.id) {
          mod.mod_type = event.value;
        }
      });
    });
    this.modifiersChange.emit(this._selectedModifiers);
  }

  upCharge(e) {
    if (e != null) {
      const estring = e.toString();
      const cutstring = estring.replace(/\$/g, '');
      const nstring = Number(cutstring)
      const f = nstring / 2;
      return f
    }
    else {
      return
    }
  }

  onSelect(event, values) {
    const modFromEvent = event.options[0];
    const selectedMod = Object.assign(new ModifierOption(), modFromEvent.value);
    const modGroup = this.modifierGroups.find(p => p.id === selectedMod.modifier_group);

    const isDependentMode = modGroup?.is_independent && this.store.feature_flag['mod_group_dependency_feature'];

    if (modFromEvent.selected) {
      const allSelectedMods = values.map(m => m.value);
      const alreadySelected = this._selectedModifiers.find(m => m.modifier.id === selectedMod.id);

      if (isDependentMode) {
        const selectedUniqueModsInGroup = this._selectedModifiers
          .filter(m => m.modifier.modifier_group === modGroup.id)
          .map(m => m.modifier.id);

        if (alreadySelected) {
          modFromEvent.selected = false;
          return;
        }

        if (modGroup.max_mods === -1 || selectedUniqueModsInGroup.length < modGroup.max_mods) {
          const mod = new Modifier();
          mod.modifier = selectedMod;
          mod.quantity = selectedMod['preselect_quantity'] ? selectedMod['preselect_quantity'] : 1;
          mod.mod_type = 'ADD';
          this._selectedModifiers.push(mod);
          this.modifiersChange.emit(this._selectedModifiers);
        } else {
          modFromEvent.selected = false;
          this.snackBar.open(`You can select up to ${modGroup.max_mods} different '${modGroup.name.toLowerCase()}'!`, "OK", {
            duration: 2000,
            verticalPosition: 'top'
          });
        }
      } else {
        const existingGroupCount = this.getModGroupCount(selectedMod.modifier_group);

        if (modGroup.max_mods === -1 || existingGroupCount < modGroup.max_mods) {
          const mod = new Modifier();
          mod.modifier = selectedMod;
          mod.quantity = selectedMod['preselect_quantity'] ? selectedMod['preselect_quantity'] : 1;
          mod.mod_type = 'ADD';
          this._selectedModifiers.push(mod);
          this.modifiersChange.emit(this._selectedModifiers);
        } else {
          modFromEvent.selected = false;
          this.snackBar.open(`Item can have ${modGroup.max_mods} '${modGroup.name.toLowerCase()}' only!`, "OK", {
            duration: 2000,
            verticalPosition: 'top'
          });
        }
      }
    } else {
      this.modifieId = null;
      this.modifierGroups[0].options.map(mod => {
        if (mod.id === selectedMod.id) {
          mod.mod_type = 'ADD';
        }
      });

      const existingCount = this.modGroupCount.get(selectedMod.modifier_group);
      if (existingCount) {
        this.modGroupCount.set(selectedMod.modifier_group, existingCount - selectedMod.quantity);
      }

      const index = this._selectedModifiers.findIndex(p => p.modifier.id === selectedMod.id);
      if (index > -1) {
        this._selectedModifiers.splice(index, 1);
      }

      // Deselecting a parent option clears its whole nested subtree so no
      // orphaned sub-modifier selections linger.
      if (this.subModifiersEnabled) {
        this.removeDescendantsOf(selectedMod.id);
      }

      this.modifiersChange.emit(this._selectedModifiers);
    }

    this.checkAllModGroupValidity();
  }


  getModGroupCount(modGroupId) {
    if (this._selectedModifiers.length > 0) {
      let qty = this._selectedModifiers.filter(f => f.modifier.modifier_group === modGroupId).map(p => p.quantity)
      if (qty.length > 0) {
        return qty.reduce((a, b) => a + b)
      } else {
        return 0
      }
    } else {
      return this._selectedModifiers.length
    }
  }

  isSelected(modifier: ModifierOption) {
    return this._selectedModifiers.find(m => m.modifier.id === modifier.id)
  }

  getSelected(modifier: ModifierOption) {
    return this._selectedModifiers.filter(m => m.modifier.id === modifier.id)[0]
  }

  // isTopping(name:string){
  //   return name.toLowerCase().includes("topping");
  // }

  trackByIndex(index: number): any {
    return index;
  }

  updateQuantity(event, modifier) {
    let selectedMod = this.getSelected(modifier);
    selectedMod.quantity = event.quantity
    this.getModGroupCount(modifier.modifier_group)
    this.modifiersChange.emit(this._selectedModifiers);
    this.checkAllModGroupValidity();
  }

  getMaxModsAvailable(modGroupId: number, modGroupMax: number, modifier) {
    const modGroup = this.modifierGroups.find(g => g.id === modGroupId);
    const isDependent =
      modGroup?.is_independent && this.store?.feature_flag?.['mod_group_dependency_feature'];
    const maxModUnits = modifier?.modifier?.max_mod_units || -1;
    const currentQty = modifier?.quantity || 0;

    if (isDependent) {
      // ✅ Case 1: is_independent === true && mod_group_dependency_feature === true
      if (maxModUnits > 0) {
        const remainingQty = maxModUnits - currentQty;
        return remainingQty > 0 ? remainingQty : 0;
      } else {
        return -1; // No limit
      }
    } else {
      // ✅ Case 2: is_independent === false OR feature flag off
      const groupCount = this.getModGroupCount(modGroupId);

      if (modGroupMax > 0) {
        const remainingQty = modGroupMax - groupCount;
        return remainingQty > 0 ? remainingQty : 0;
      } else {
        return -1; // No group limit
      }
    }
  }


  allModIsPOS(modOption) {
    return modOption.some(modOption => !modOption.is_pos_only);
  }


  onPreModChange(event, modifier) {
    this.modifieId = modifier.id;
    let selectedMod = this.getSelected(modifier);
    selectedMod.modifier.mod_tag = event.value;
    this.modifiersChange.emit(this._selectedModifiers);
  }
}
